import {
  uyPlatformFeeNetMinor,
  uyVatInGrossMinor,
  uyVatOnNetMinor,
} from "@gestionalo/shared-types";
import { useMemo, useState } from "react";

/* Mirrors QUOTE_REQUIRED_DOCUMENTS_MAX in the backend DTO. Duplicated rather
   than imported because the backend is not a dependency of the portal; the
   form refusing what the server refuses is the point, and a drift here shows
   up as a 400 the professional can act on rather than silent truncation. */
const QUOTE_REQUIRED_DOCUMENTS_MAX = 10;
import { useTranslation } from "react-i18next";
import { Loader2, Plus, X } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { parseUyAmount } from "@/lib/parseUyAmount";
import type { QuoteLineItemPayload } from "@/services/notaryRequestsService";
import type { ContactDetailKind } from "@/services/quoteRequestsService";
import { ContactBlockedNotice } from "./contact-blocked-notice";
import {
  QUOTE_LINE_ITEM_KINDS,
  QUOTE_LINE_ITEM_KIND_LABEL_KEYS,
  type QuoteLineItemKind,
} from "@/services/quoteLineItems";

/**
 * The unit a line's amount is typed in. `UYU` means the number IS the price;
 * the two UR options mean it is a quantity the server converts at submission.
 *
 * One select instead of a pesos/UR mode toggle plus a second index selector:
 * the professional makes one choice, and the three options are the three
 * things that choice can be.
 */
export type QuoteLineUnit = "UYU" | "dgi" | "caja_notarial";

export type QuoteLineKind = QuoteLineItemKind;

/**
 * One declared requirement, as the form holds it.
 *
 * No `source` field: provenance is the server's to decide, so the form never
 * carries a claim about it (spec 18c82ff1 control C-3). What the form DOES
 * track is `seeded`, purely to render "sugerido" beside items the intake
 * proposed — a local display concern that is never sent.
 */
export interface QuoteRequiredDocumentDraft {
  label: string;
  kind: "file" | "text";
  documentType: string | null;
  seeded: boolean;
}

export interface QuoteLineDraft {
  /** Stable React key — rows are removable, so the index is not one. */
  key: string;
  description: string;
  kind: QuoteLineKind;
  /** As typed: pesos when unit is UYU, UR units otherwise. */
  amount: string;
  unit: QuoteLineUnit;
}

export interface QuoteFormConfig {
  platformFeePercent: number;
  dgi: { urValueMinor: number | null; referenceLabel: string | null };
  cajaNotarial: { urValueMinor: number | null; referenceLabel: string | null };
}

/** A new empty line. Defaults to honorarios in pesos — the common case. */
// createQuoteLineDraft lives in ./quote-line-drafts, a module with no React in
// it, so a node-run spec can import the draft helpers without dragging this
// component's whole import graph (and @gestionalo/shared-types' ESM build)
// along. Re-exported here because callers have always imported it from this
// file.
export { createQuoteLineDraft } from "./quote-line-drafts";
import { createQuoteLineDraft } from "./quote-line-drafts";

/**
 * A line's amount as a number, or null when it is blank or unreadable.
 *
 * Thousands grouping is allowed only on a UYU line, where `1.500` is fifteen
 * hundred. On a UR line the number is a QUANTITY, so `2.5` has to stay two
 * and a half units rather than being read as a grouped 2500.
 */
function parseAmount(line: QuoteLineDraft): number | null {
  return parseUyAmount(line.amount, { allowThousandsGrouping: line.unit === "UYU" });
}

/**
 * Drafts → request payload. No UR conversion here on purpose: the server
 * resolves the index at submission (spec rfq-structured-quotes §3d), so a
 * client-side amount would be a second opinion on the price.
 */
export function buildQuoteLineItemsPayload(lines: QuoteLineDraft[]): QuoteLineItemPayload[] {
  return lines.map((line) => {
    // Same parse the form previewed with — a second opinion here would submit
    // a price the professional never saw.
    //
    // Throws rather than coercing to 0: this function is exported, and the
    // only thing standing between an unreadable amount and a zero-priced
    // quote line is the caller having honoured the disabled submit button. A
    // future caller that does not should fail here, loudly, not bill someone
    // nothing.
    const value = parseAmount(line);
    if (value == null) {
      throw new Error(
        `Unreadable amount on quote line "${line.description}": ${line.amount}`,
      );
    }
    if (line.unit === "UYU") {
      return {
        description: line.description.trim(),
        kind: line.kind,
        amountMinor: Math.round(value * 100),
      };
    }
    return {
      description: line.description.trim(),
      kind: line.kind,
      urUnits: value,
      urType: line.unit,
    };
  });
}

const UNIT_LABEL_KEYS: Record<QuoteLineUnit, string> = {
  UYU: "notary.requestDetail.quoteLineUnitUyu",
  dgi: "notary.requestDetail.quoteLineUnitDgi",
  caja_notarial: "notary.requestDetail.quoteLineUnitCaja",
};

/**
 * A field that lives in a table cell: the normal border, just shorter than a
 * standalone input.
 *
 * The first version was borderless at rest, copying QuickBooks and Xero. That
 * misread them: their fields work borderless because their tables draw
 * VERTICAL grid lines, so the cell boundary marks the field even when the
 * field does not. This table has row dividers only, so borderless left
 * nothing to click at — the quantity cell in particular read as empty space.
 * Melio and HubSpot keep bordered inputs inside the table for exactly this
 * reason, and a field you cannot see is a worse failure than one border too
 * many.
 */
const CELL_FIELD = "h-9 shadow-none";

interface PricedLine {
  amountMinor: number | null;
  urUnavailable: boolean;
  /**
   * The professional typed something the field cannot read as a positive
   * amount. Distinct from an empty field: both block submission, but only
   * this one deserves a red border, because the row LOOKS filled in.
   */
  amountInvalid: boolean;
}

function priceLine(line: QuoteLineDraft, config: QuoteFormConfig | null): PricedLine {
  const typed = line.amount.trim().length > 0;
  const value = parseAmount(line);
  if (value == null || value <= 0) {
    return { amountMinor: null, urUnavailable: false, amountInvalid: typed };
  }
  if (line.unit === "UYU") {
    return { amountMinor: Math.round(value * 100), urUnavailable: false, amountInvalid: false };
  }
  const urValueMinor =
    line.unit === "dgi" ? config?.dgi.urValueMinor : config?.cajaNotarial.urValueMinor;
  if (urValueMinor == null) {
    return { amountMinor: null, urUnavailable: true, amountInvalid: false };
  }
  return {
    amountMinor: Math.round(value * urValueMinor),
    urUnavailable: false,
    amountInvalid: false,
  };
}

export interface QuoteSubmissionFieldsProps {
  lines: QuoteLineDraft[];
  onLinesChange: (lines: QuoteLineDraft[]) => void;
  /** Null while loading, or when the endpoint failed — UR lines are then unpriceable. */
  config: QuoteFormConfig | null;
  /**
   * Does this job end in paper the professional has to deliver?
   *
   * A CONTROL AGAIN, owned by the professional (spec f462e7d6 3.6). It was
   * briefly derived from the request's own intake answer, on the reasoning
   * that physicality "is not theirs to decide" — which contradicted decisions
   * 851922e2 and 84103aaa, and left a trámite that triage called digital with
   * no way to become physical: the awarded child spawns digital_only and
   * mark-ready then refuses it, so the professional holds paper the platform
   * will not let them deliver.
   *
   * It drives the envio prompt below and, server-side, the delivery fee added
   * to the consumer's total.
   */
  requiresPhysicalDelivery: boolean;
  onRequiresPhysicalDeliveryChange: (value: boolean) => void;
  /**
   * Whether the request has a delivery address at all. False only on an RFQ
   * created before the address step became mandatory — the server refuses a
   * physical quote on one (RES-4), so the form says why before they submit.
   */
  hasDeliveryAddress?: boolean;
  /**
   * What the consumer will be charged to ship THIS request's document, from
   * the request detail. Null when the address could not be zoned.
   */
  deliveryFeeMinor?: number | null;
  estimatedDays: string;
  onEstimatedDaysChange: (value: string) => void;
  /**
   * What this professional says the job needs from the consumer (spec
   * 18c82ff1 §5a). Seeded by the caller from the intake's anticipation, then
   * owned by the professional — the platform guessed before anyone had seen
   * the request, and they are the one who knows.
   */
  requiredDocuments: QuoteRequiredDocumentDraft[];
  onRequiredDocumentsChange: (next: QuoteRequiredDocumentDraft[]) => void;
  onSubmit: () => void;
  disabled?: boolean;
  /**
   * Whether this form is rewriting a quote that was already sent.
   *
   * It is the SAME form either way — the endpoint upserts — so nothing about
   * the fields changes. What changes is what the professional is promised:
   * "Enviar cotización" on a bid that already exists is wrong twice over, and
   * without a way back out, someone who opened Editar just to re-read their
   * own figures could leave only by saving a quote they never meant to touch.
   */
  isEditing?: boolean;
  /** Leaves edit mode, discarding the draft. Required when `isEditing`. */
  onCancelEdit?: () => void;
  /**
   * Whether the submission is in flight RIGHT NOW. Deliberately not the same
   * fact as `disabled`: that one also gates the line inputs and can be true
   * for reasons that have nothing to do with sending, and a form frozen for
   * one of those must not show a spinner claiming it is being sent.
   */
  isSubmitting?: boolean;
  /**
   * Kinds the last submission was refused for, or null. Owned by the page,
   * like the clarification composer's: only the page sees the mutation error.
   */
  blockedKinds?: ContactDetailKind[] | null;
}

/**
 * The professional's bid form: an itemised breakdown, what the client pays,
 * and what they themselves receive (spec rfq-structured-quotes, phase Q2).
 *
 * The totals are a preview of arithmetic the SERVER does — it recomputes the
 * total from the lines at submission (§3b) — which is why a UR line whose
 * index is unavailable shows no amount rather than a guess.
 */
export function QuoteSubmissionFields({
  lines,
  onLinesChange,
  config,
  requiresPhysicalDelivery,
  onRequiresPhysicalDeliveryChange,
  hasDeliveryAddress = true,
  deliveryFeeMinor,
  estimatedDays,
  onEstimatedDaysChange,
  requiredDocuments,
  onRequiredDocumentsChange,
  onSubmit,
  disabled = false,
  isEditing = false,
  onCancelEdit,
  isSubmitting = false,
  blockedKinds = null,
}: QuoteSubmissionFieldsProps): JSX.Element {
  const { t } = useTranslation();

  const priced = useMemo(
    () => lines.map((line) => ({ line, ...priceLine(line, config) })),
    [lines, config],
  );

  const totalMinor = priced.reduce((sum, p) => sum + (p.amountMinor ?? 0), 0);

  /** Per-kind subtotals, in the constant's order, skipping kinds not used. */
  const subtotals = useMemo(
    () =>
      QUOTE_LINE_ITEM_KINDS.map((kind) => ({
        kind,
        amountMinor: priced
          .filter((p) => p.line.kind === kind)
          .reduce((sum, p) => sum + (p.amountMinor ?? 0), 0),
      })).filter((row) => row.amountMinor > 0),
    [priced],
  );

  const commissionBaseMinor =
    subtotals.find((row) => row.kind === "honorarios")?.amountMinor ?? 0;
  const platformFeePercent = config?.platformFeePercent ?? null;
  // ADDED to the consumer's total, not taken out of the professional's
  // (spec 76e23408 D3). The professional receives `totalMinor` whole.
  // NET PLUS IVA, matching quoteConsumerFeeMinor on the server to the
  // centesimo — the percentage is what Gestionalo keeps and 22% is added on
  // top. It used to be the whole charge with the IVA inside, which made a
  // stated 10% actually worth 8,197%. Rounded in the same order as the server
  // (net first, then its IVA) because rounding a gross and dividing it back
  // down does not always return the net it came from.
  // THE SHARED FUNCTION, not a local multiplication, and that is the whole
  // point of this import. This preview and the server's charge are now the
  // same code: when the fee's base moved from the honorarios to the full price
  // the server changed and this line did not, and a professional was shown a
  // total 24,40 below what their client would be charged.
  //
  // commissionBaseMinor stays below for the IVA decomposition, which is a
  // different question: timbres carry no IVA and must not be decomposed.
  const platformFeeNetMinor =
    platformFeePercent == null ? null : uyPlatformFeeNetMinor(totalMinor, platformFeePercent);
  // Shown on its own line, so it is derived rather than re-rounded: taking
  // 22% of the net a second time for display could disagree by a centesimo
  // with the IVA actually inside the gross, and the column has to add up.
  const platformFeeIvaMinor =
    platformFeeNetMinor == null ? 0 : uyVatOnNetMinor(platformFeeNetMinor);
  /* Free text in a field that is about to replace free text, so it is kept
     deliberately narrow: one line, added as a labelled item, never a
     paragraph. The 160-char ceiling matches the DTO so the form refuses what
     the server would refuse rather than round-tripping a 400. */
  const [newRequiredDocument, setNewRequiredDocument] = useState("");
  const addRequiredDocument = (): void => {
    const label = newRequiredDocument.trim().slice(0, 160);
    if (label.length < 2) return;
    if (requiredDocuments.length >= QUOTE_REQUIRED_DOCUMENTS_MAX) return;
    onRequiredDocumentsChange([
      ...requiredDocuments,
      /* `kind: "file"` is the default because a professional adding a line
         here is almost always naming a document; the text items come from the
         intake, which asks the model to distinguish them. Nothing is lost if
         it is wrong — kind only changes the hint shown to the consumer. */
      { label, kind: "file", documentType: null, seeded: false },
    ]);
    setNewRequiredDocument("");
  };

  const platformFeeMinor =
    platformFeeNetMinor == null ? null : platformFeeNetMinor + platformFeeIvaMinor;
  // The IVA already inside the honorarios, shown as its own line so the
  // breakdown decomposes instead of hiding it (D6). Timbres carry none.
  const ivaMinor = uyVatInGrossMinor(commissionBaseMinor);
  const honorariosNetMinor = commissionBaseMinor - ivaMinor;
  // NO SEPARATE DELIVERY TERM (decision 2a18572). Shipping is an `envio` LINE
  // the professional writes themselves, so it is already inside `totalMinor`
  // — adding it again here would show them a total 270 higher than the card
  // is charged, which is the mirror of the bug the old incremental per-bid
  // amount existed to avoid.
  //
  // `deliveryFeeMinor` survives as a SUGGESTION only: what the platform thinks
  // this destination costs to ship, offered as a prefill below.
  const totalToChargeMinor =
    platformFeeMinor == null ? null : totalMinor + platformFeeMinor;
  const professionalReceivesMinor = totalMinor;

  /**
   * Has the professional actually priced the shipping this job needs?
   *
   * The request says it produces paper; only they can say what moving it
   * costs. Until they add the line, nobody is charging for the parcel and
   * nobody is being reimbursed for it — so the form says so rather than
   * letting the quote go out silently short.
   */
  const hasEnvioLine = priced.some(
    (p) => p.line.kind === "envio" && (p.amountMinor ?? 0) > 0,
  );
  /*
    AND ONLY WHEN SHIPPING CAN ACTUALLY BE PRICED. Without the address
    condition this fired in the one case it cannot be acted on: the form told
    the professional physical delivery was impossible here and, directly
    beneath, offered a suggested price "para esta zona" — for a zone that
    cannot exist without an address — and a button to add the line anyway.
    Two notices, contradicting each other, about the same toggle.
  */
  const envioMissing = requiresPhysicalDelivery && !hasEnvioLine;

  const lineIsComplete = (p: PricedLine & { line: QuoteLineDraft }): boolean =>
    p.line.description.trim().length >= 2 && p.amountMinor != null;
  const everyLineComplete = priced.every(lineIsComplete);
  const hasUnavailableUr = priced.some((p) => p.urUnavailable);
  const hasEta = estimatedDays.trim().length > 0;
  /*
    NO LONGER A BLOCKER (spec e370e36f). A physical quote on a request with no
    destination used to be refused server-side, so the button went dead here to
    save the professional a 400. That refusal is gone: the address is collected
    from the CONSUMER at award instead, which is the only place it can be
    answered rather than merely rejected.

    The professional is told the destination is still open — see the notice
    below — but is not stopped. Stopping them was what made an addressless
    request unrepairable: nobody could declare paper was involved, so nothing
    downstream knew to ask for an address.
  */
  const addressStillOpen = requiresPhysicalDelivery && !hasDeliveryAddress;
  const canSubmit =
    everyLineComplete && commissionBaseMinor > 0 && hasEta && !hasUnavailableUr;

  // A disabled button with no stated reason is a dead end. One message, for
  // the first unmet condition, in the order a professional would fix them.
  //
  // The line messages name the ROW. "Completá cada línea" on a five-row
  // breakdown makes the professional audit all five; a notary wrote in
  // because the button would not enable and this text did not tell them
  // where to look.
  const firstUnreadableAmount = priced.findIndex((p) => p.amountInvalid);
  const firstIncompleteLine = priced.findIndex((p) => !lineIsComplete(p));
  let blockedReason: string | null = null;
  if (hasUnavailableUr) blockedReason = t("notary.requestDetail.quoteBlockedUrUnavailable");
  else if (firstUnreadableAmount >= 0)
    blockedReason = t("notary.requestDetail.quoteBlockedInvalidAmount", {
      row: firstUnreadableAmount + 1,
    });
  else if (firstIncompleteLine >= 0)
    blockedReason = t("notary.requestDetail.quoteBlockedIncompleteLine", {
      row: firstIncompleteLine + 1,
    });
  else if (commissionBaseMinor <= 0)
    blockedReason = t("notary.requestDetail.quoteBlockedNoHonorarios");
  else if (!hasEta) blockedReason = t("notary.requestDetail.quoteBlockedNoEta");


  const updateLine = (key: string, patch: Partial<QuoteLineDraft>): void => {
    onLinesChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  // Stated once, below the table, for each index a line actually uses. Per
  // row this was noise repeated on every UR line and it wrapped to two lines,
  // making those rows taller than their neighbours.
  const urRatesInUse = useMemo(() => {
    const units = new Set(lines.map((l) => l.unit).filter((u) => u !== "UYU"));
    return [...units].map((unit) => {
      const source = unit === "dgi" ? config?.dgi : config?.cajaNotarial;
      return {
        unit,
        label: t(UNIT_LABEL_KEYS[unit]),
        urValueMinor: source?.urValueMinor ?? null,
        referenceLabel: source?.referenceLabel ?? null,
      };
    });
  }, [lines, config, t]);

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">
          {t(
            isEditing
              ? "notary.requestDetail.quoteUpdateTitle"
              : "notary.requestDetail.quoteSubmissionTitle",
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t("notary.requestDetail.quoteBreakdownHint")}
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* One bordered container with divided rows, rather than inputs
            floating on the card. Four controls per line with nothing holding
            them together read as a form matrix; a bounded list with a header
            and an add-row reads as the document it is. */}
        {/* A real table, with the fields living INSIDE its cells. Every
            invoice builder worth copying (QuickBooks, Xero, Airwallex, Melio,
            HoneyBook, HubSpot) does this, and for the same reason: bordered
            inputs stacked inside a bordered container is boxes-inside-boxes,
            and at this width the concept field alone was 870px of empty
            outline. The cell is the field — its border appears on hover and
            focus and is absent at rest, so the row reads as data. */}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t("notary.requestDetail.quoteLineConceptLabel")}
                </th>
                <th scope="col" className="w-[10rem] px-2 py-2 text-left font-medium">
                  {t("notary.requestDetail.quoteLineKindLabel")}
                </th>
                {/* NEUTRAL, because this header sits above rows that can each
                    carry a different unit. With "Pesos (UYU)" the number is an
                    amount and "Cantidad" read as 2313 items; with UR it really
                    is a quantity, so "Importe" would be wrong there. One column,
                    two meanings — the only honest shared word is the broader
                    one, and the exact word is given per row on the field's own
                    label below. */}
                <th scope="col" className="w-[6.5rem] px-2 py-2 text-right font-medium">
                  {t("notary.requestDetail.quoteLineValueLabel")}
                </th>
                <th scope="col" className="w-[10.5rem] px-2 py-2 text-left font-medium">
                  {t("notary.requestDetail.quoteLineUnitLabel")}
                </th>
                <th scope="col" className="w-[8rem] px-3 py-2 text-right font-medium">
                  {t("notary.requestDetail.quoteLineAmountLabel")}
                </th>
                <th scope="col" className="w-10 px-1 py-2">
                  <span className="sr-only">{t("notary.requestDetail.quoteLineRemove")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const priceForLine = priceLine(line, config);
                return (
                  <tr key={line.key} className="border-b border-border last:border-b-0">
                    <td className="px-1 py-1">
                      <Label htmlFor={`${line.key}-description`} className="sr-only">
                        {t("notary.requestDetail.quoteLineConceptLabel")}
                      </Label>
                      <Input
                        id={`${line.key}-description`}
                        className={CELL_FIELD}
                        value={line.description}
                        onChange={(e) => updateLine(line.key, { description: e.target.value })}
                        placeholder={t("notary.requestDetail.quoteLineConceptPlaceholder")}
                        disabled={disabled}
                      />
                    </td>

                    <td className="px-1 py-1">
                      <Label htmlFor={`${line.key}-kind`} className="sr-only">
                        {t("notary.requestDetail.quoteLineKindLabel")}
                      </Label>
                      <Select
                        value={line.kind}
                        onValueChange={(v) => updateLine(line.key, { kind: v as QuoteLineKind })}
                        disabled={disabled}
                      >
                        <SelectTrigger id={`${line.key}-kind`} className={CELL_FIELD}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUOTE_LINE_ITEM_KINDS.map((kind) => (
                            <SelectItem key={kind} value={kind}>
                              {t(QUOTE_LINE_ITEM_KIND_LABEL_KEYS[kind])}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    <td className="px-1 py-1">
                      {/* The field's OWN label, per row, so it can say the
                          exact thing the header cannot. Same expression both
                          mobile apps already use (quote_bid_screen.dart and
                          bid.tsx): pesos means this box is the importe,
                          anything else means it is a count of UR. A screen
                          reader announces this, not the column header. */}
                      <Label htmlFor={`${line.key}-amount`} className="sr-only">
                        {t(
                          line.unit === "UYU"
                            ? "notary.requestDetail.quoteLineAmountLabel"
                            : "notary.requestDetail.quoteLineQuantityLabel",
                        )}
                      </Label>
                      {/* Text, not type="number". A native number input
                          REJECTS the comma keystroke, so a notary typing
                          1.500,00 — the way prices are written here — was
                          left with an empty value in a field that looked
                          filled, and a submit button that never enabled.
                          parseUyAmount reads both conventions, and the
                          Amount cell echoes what we understood. */}
                      <Input
                        id={`${line.key}-amount`}
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        aria-invalid={priceForLine.amountInvalid || undefined}
                        aria-describedby={
                          priceForLine.amountInvalid ? `${line.key}-amount-error` : undefined
                        }
                        className={`${CELL_FIELD} text-right tabular-nums ${
                          priceForLine.amountInvalid
                            ? "border-destructive focus-visible:ring-destructive"
                            : ""
                        }`}
                        value={line.amount}
                        onChange={(e) => updateLine(line.key, { amount: e.target.value })}
                        disabled={disabled}
                      />
                    </td>

                    <td className="px-1 py-1">
                      <Label htmlFor={`${line.key}-unit`} className="sr-only">
                        {t("notary.requestDetail.quoteLineUnitLabel")}
                      </Label>
                      <Select
                        value={line.unit}
                        onValueChange={(v) => updateLine(line.key, { unit: v as QuoteLineUnit })}
                        disabled={disabled}
                      >
                        <SelectTrigger id={`${line.key}-unit`} className={CELL_FIELD}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(UNIT_LABEL_KEYS) as QuoteLineUnit[]).map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {t(UNIT_LABEL_KEYS[unit])}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* Computed, never typed — the Qty x Rate = Amount column
                        every one of those builders has. It is where a UR line
                        stops being abstract: 2 UR reads as $ 3.683,12 in the
                        same row. */}
                    <td className="px-3 py-1 text-right tabular-nums text-foreground">
                      {priceForLine.amountMinor != null ? (
                        formatUyMinor(priceForLine.amountMinor)
                      ) : priceForLine.amountInvalid ? (
                        <span id={`${line.key}-amount-error`} className="text-xs text-destructive">
                          {t("notary.requestDetail.quoteLineAmountUnreadable")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>

                    <td className="px-1 py-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => onLinesChange(lines.filter((l) => l.key !== line.key))}
                        disabled={disabled || lines.length === 1}
                        aria-label={t("notary.requestDetail.quoteLineRemove")}
                      >
                        <X />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* THE PHYSICALITY DECLARATION (spec f462e7d6 3.6).
              Above the shipping prompt because it is what turns that prompt
              on: the professional says the job ends in paper, then prices
              moving it. Seeded from what the intake guessed and theirs to
              correct — whether a trámite yields a testimonio is their
              knowledge, not the triage agent's (decisions 851922e2,
              84103aaa). */}
          <div className="space-y-2 border-t border-border px-3 py-3">
            <div className="flex items-start justify-between gap-4">
              <Label
                htmlFor="quote-requires-physical"
                className="cursor-pointer text-sm font-medium leading-6 text-foreground"
              >
                {t("notary.requestDetail.quotePhysicalToggleLabel")}
              </Label>
              <Switch
                id="quote-requires-physical"
                checked={requiresPhysicalDelivery}
                onCheckedChange={onRequiresPhysicalDeliveryChange}
                disabled={disabled}
              />
            </div>
            <p className="max-w-[68ch] text-xs leading-relaxed text-muted-foreground">
              {requiresPhysicalDelivery
                ? t("notary.requestDetail.quotePhysicalToggleOnHint")
                : t("notary.requestDetail.quotePhysicalToggleOffHint")}
            </p>
          </div>

          {/* THE SHIPPING PROMPT (decision 2a18572).
              Shown only when the request produces paper and no envio line
              prices it yet. The amount is a SUGGESTION from the platform's
              per-zone rate, prefilled into a real editable line rather than
              applied behind their back — what a professional charges is their
              own business, and they may ship it cheaper, dearer, or at cost.
              `deliveryFeeMinor` is null when the consumer's address could not
              be zoned, and then the prompt still appears without a figure:
              they still have to price it, we just cannot suggest what. */}
          {/* ONE NOTICE, TWO MODES, and the first mode changed meaning in
              spec e370e36f. With no address the strip no longer says physical
              delivery is impossible — it says the destination is still open
              and will be settled when the consumer accepts. The professional
              prices the envío line themselves with no suggestion, because a
              suggestion needs a zone and there is no address to resolve one
              from. With an address it is the ordinary shipping prompt below. */}
          {addressStillOpen ? (
            <div className="border-t border-border bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
              <p className="max-w-[68ch] text-xs leading-relaxed text-amber-900 dark:text-amber-200">
                {t("notary.requestDetail.quotePhysicalAddressPending")}
              </p>
            </div>
          ) : envioMissing ? (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
              <p className="text-xs text-amber-900 dark:text-amber-200">
                {deliveryFeeMinor != null && deliveryFeeMinor > 0
                  ? t("notary.requestDetail.quoteEnvioPromptWithSuggestion", {
                      amount: formatUyMinor(deliveryFeeMinor),
                    })
                  : t("notary.requestDetail.quoteEnvioPrompt")}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                disabled={disabled || lines.length >= 40}
                onClick={() =>
                  onLinesChange([
                    ...lines,
                    {
                      ...createQuoteLineDraft(),
                      kind: "envio",
                      description: t("quoteBreakdown.kindEnvio"),
                      amount:
                        deliveryFeeMinor != null && deliveryFeeMinor > 0
                          ? (deliveryFeeMinor / 100).toFixed(2)
                          : "",
                    },
                  ])
                }
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                {t("notary.requestDetail.quoteEnvioAdd")}
              </Button>
            </div>
          ) : null}

          {/* A small text button under the table, not a full-width bar. Every
              reference does it this way; the bar carried more weight than the
              action deserves. */}
          <div className="border-t border-border px-2 py-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-muted-foreground hover:text-foreground"
              onClick={() => onLinesChange([...lines, createQuoteLineDraft()])}
              disabled={disabled || lines.length >= 40}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t("notary.requestDetail.quoteLineAdd")}
            </Button>
          </div>

          {totalMinor > 0 ? (
            <div className="border-t border-border bg-muted/20 px-3 py-3">
              <dl className="ml-auto w-full max-w-xs space-y-1.5">
                {/* Honorarios are shown NET with the IVA broken out beside
                    them (D6). Timbres and gastos are pass-through and carry no
                    IVA, so they print as entered. */}
                {subtotals.map((row) => (
                  <div key={row.kind}>
                    <div className="flex items-baseline justify-between gap-4 text-xs">
                      <dt className="text-muted-foreground">
                        {t(QUOTE_LINE_ITEM_KIND_LABEL_KEYS[row.kind])}
                      </dt>
                      <dd className="tabular-nums text-muted-foreground">
                        {formatUyMinor(
                          row.kind === "honorarios" ? honorariosNetMinor : row.amountMinor,
                        )}
                      </dd>
                    </div>
                    {row.kind === "honorarios" && ivaMinor > 0 ? (
                      <div className="flex items-baseline justify-between gap-4 text-xs">
                        <dt className="text-muted-foreground">
                          {t("notary.requestDetail.quoteIvaLine")}
                        </dt>
                        <dd className="tabular-nums text-muted-foreground">
                          {formatUyMinor(ivaMinor)}
                        </dd>
                      </div>
                    ) : null}
                  </div>
                ))}

                {/* The additions, then the two numbers that matter: what the
                    client pays and what the professional takes home. They are
                    now independent — the fee is ADDED to the first instead of
                    subtracted from the second (D3). */}
                {/* The commission is DECOMPOSED, the same way honorarios are
                    above it: the net on one line, its IVA on the next. It used
                    to be a single row carrying the whole gross and explaining
                    itself in its own label ("10% + IVA"), which is the one row
                    on this breakdown that had to be read rather than just
                    added up. Now every line is a plain amount and the rule is
                    visible in the arithmetic instead of in parentheses. */}
                {platformFeeNetMinor != null ? (
                  <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5 text-xs">
                    <dt className="text-muted-foreground">
                      {t("notary.requestDetail.quotePlatformFeeLine")}
                    </dt>
                    <dd className="tabular-nums text-muted-foreground">
                      +{formatUyMinor(platformFeeNetMinor)}
                    </dd>
                  </div>
                ) : null}

                {platformFeeIvaMinor > 0 ? (
                  <div className="flex items-baseline justify-between gap-4 text-xs">
                    <dt className="text-muted-foreground">
                      {t("notary.requestDetail.quoteIvaLine")}
                    </dt>
                    <dd className="tabular-nums text-muted-foreground">
                      +{formatUyMinor(platformFeeIvaMinor)}
                    </dd>
                  </div>
                ) : null}

                {totalToChargeMinor != null ? (
                  <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5">
                    <dt className="text-sm text-muted-foreground">
                      {t("notary.requestDetail.quoteTotalLabelIvaIncluded")}
                    </dt>
                    <dd className="text-lg font-semibold tabular-nums text-foreground">
                      {formatUyMinor(totalToChargeMinor)}
                    </dd>
                  </div>
                ) : null}

                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-sm font-medium text-foreground">
                    {t("notary.requestDetail.quoteNetLabel")}
                  </dt>
                  <dd className="text-sm font-semibold tabular-nums text-primary">
                    {formatUyMinor(professionalReceivesMinor)}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}
        </div>

        {urRatesInUse.length > 0 ? (
          <ul className="space-y-1">
            {urRatesInUse.map((rate) => (
              <li key={rate.unit} className="text-xs text-muted-foreground">
                {rate.urValueMinor != null
                  ? `${rate.label} — ${t("notary.requestDetail.quoteUrReference", {
                      value: formatUyMinor(rate.urValueMinor),
                      period: rate.referenceLabel ?? "",
                    })}`
                  : `${rate.label} — ${t("notary.requestDetail.quoteUrUnavailableShort")}`}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="space-y-4 border-t border-border pt-6">
          {/*
            TWO checkboxes used to stand here and both are gone.

            The IVA one asked the professional to classify their own price per
            quote, and the answer silently changed what the CLIENT was charged
            — unticking it added 22% of the honorarios, taking a 1.500 + 500
            quote from 2.000 to 2.330 — behind a hint reading "solo
            informativo" (spec 76e23408 D6). That fact is captured once now, at
            onboarding, as the fiscal declaration.

            The physical-document one went the same way for the same reason
            (spec 8c2aab49 D2): the INTAKE decides whether a trámite ends in
            paper, before anyone bids, because shipping has to be priced into
            the quotes the consumer compares. By the time this form is open the
            consumer has already been asked, given a delivery address, and seen
            the shipping on every quote — so asking the professional to declare
            it again could only contradict them.

            Its hint had also gone stale in a way that mattered: it promised
            "Gestionalo se encarga del traslado; tu precio no cambia", and
            under D1 the professional dispatches the parcel themselves and IS
            reimbursed the shipping. Both halves became untrue.

            What is NOT built, and is named rather than quietly dropped: the
            professional can no longer CORRECT a wrong intake answer on a job
            that turns out digital. That is control C-4 and it needs the flag
            to become a correction field rather than a second source of truth.
          */}

          <div className="space-y-2 sm:max-w-[12rem]">
            <Label htmlFor="quote-submission-eta">
              {t("notary.requestDetail.quoteSubmissionEtaLabel")}
            </Label>
            <Input
              id="quote-submission-eta"
              type="number"
              inputMode="numeric"
              min="0"
              className="tabular-nums"
              value={estimatedDays}
              onChange={(e) => onEstimatedDaysChange(e.target.value)}
              placeholder={t("notary.requestDetail.quoteSubmissionEtaPlaceholder")}
              disabled={disabled}
            />
          </div>

          {/* WHAT THE JOB NEEDS, as data rather than as a paragraph.

              The professional used to write this into the free-text message,
              where it could not be compared between bids and — measured on
              production quote d9753dbb — could contradict the priced line
              items it sat next to. Seeded from the intake's anticipation so
              the common case is reviewing a list rather than writing one
              (spec 18c82ff1 §5a). */}
          <div className="space-y-2">
            <Label>{t("notary.requestDetail.quoteRequiredDocumentsLabel")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("notary.requestDetail.quoteRequiredDocumentsHelp")}
            </p>

            {requiredDocuments.length > 0 ? (
              <ul className="space-y-2">
                {requiredDocuments.map((doc, index) => (
                  <li
                    key={`${doc.label}-${index}`}
                    className="flex items-start justify-between gap-2 rounded-md border border-border bg-background px-3 py-2"
                  >
                    <div className="min-w-0 space-y-1">
                      <p className="break-words text-sm text-foreground">{doc.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          doc.kind === "text"
                            ? "notary.requestDetail.quoteRequiredDocumentsKindText"
                            : "notary.requestDetail.quoteRequiredDocumentsKindFile",
                        )}
                        {doc.seeded
                          ? ` · ${t("notary.requestDetail.quoteRequiredDocumentsSeeded")}`
                          : ""}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() =>
                        onRequiredDocumentsChange(
                          requiredDocuments.filter((_, i) => i !== index),
                        )
                      }
                    >
                      {t("notary.requestDetail.quoteRequiredDocumentsRemove")}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("notary.requestDetail.quoteRequiredDocumentsEmpty")}
              </p>
            )}

            {requiredDocuments.length < QUOTE_REQUIRED_DOCUMENTS_MAX ? (
              <div className="flex items-start gap-2">
                <Input
                  id="quote-required-document-new"
                  value={newRequiredDocument}
                  onChange={(e) => setNewRequiredDocument(e.target.value)}
                  placeholder={t("notary.requestDetail.quoteRequiredDocumentsPlaceholder")}
                  disabled={disabled}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addRequiredDocument();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={disabled || newRequiredDocument.trim().length < 2}
                  onClick={addRequiredDocument}
                >
                  {t("notary.requestDetail.quoteRequiredDocumentsAdd")}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("notary.requestDetail.quoteRequiredDocumentsFull", {
                  max: QUOTE_REQUIRED_DOCUMENTS_MAX,
                })}
              </p>
            )}
          </div>

        </div>

        {/* A footer, not a button hanging under the last field: the reason it
            is unavailable sits beside it rather than underneath as an
            error-looking line.

            "Beside" used to mean justify-between, which on a full-width card
            put the reason at the far LEFT and the button at the far RIGHT —
            far enough apart to read as unrelated boilerplate. They are one
            right-aligned group now, so the sentence explaining the disabled
            button is the last thing read before it. */}
        {/* Above the footer, not in it: a refused quote is a thing to fix in
            the fields above, and the reason has to stay on screen while the
            professional edits them. */}
        {blockedKinds ? (
          <ContactBlockedNotice
            kinds={blockedKinds}
            titleKey="notary.requestDetail.quoteSubmissionBlockedTitle"
          />
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-border pt-4">
          {blockedReason ? (
            <p
              id="quote-submission-blocked-reason"
              role="status"
              className="text-sm text-muted-foreground"
            >
              {blockedReason}
            </p>
          ) : null}
          {/* A greyed button and nothing else is indistinguishable from a dead
              one. The professional has just spent minutes itemising a bid, and
              the one moment they need to be told something is happening is the
              moment the form stops responding. Same spinner-plus-label pattern
              the rest of this page already uses, and the same state mobile's
              bid screen has carried all along. */}
          {/* THE WAY OUT. Without it, a professional who opened Editar to
              re-read their own figures could leave only by saving a quote they
              never meant to change — the draft is discarded here, and the live
              quote is untouched because nothing was sent. */}
          {isEditing && onCancelEdit ? (
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={onCancelEdit}
            >
              {t("notary.requestDetail.quoteUpdateCancel")}
            </Button>
          ) : null}
          <Button
            onClick={onSubmit}
            disabled={disabled || !canSubmit}
            aria-describedby={blockedReason ? "quote-submission-blocked-reason" : undefined}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                {t(
                  isEditing
                    ? "notary.requestDetail.quoteUpdateSubmitting"
                    : "notary.requestDetail.quoteSubmissionSubmitting",
                )}
              </>
            ) : (
              t(
                isEditing
                  ? "notary.requestDetail.quoteUpdateAction"
                  : "notary.requestDetail.quoteSubmissionTitle",
              )
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
